import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'

import * as dms from '../../dms'
import * as me from '../../me'
import * as routeBound from '../../route-bound'
import * as functions from '../full/functions'
// NOTE: Imported directly instead of through the user-access index, which
// would create an import cycle via standard-users
import * as surveyAdmins from '../user-access/survey-admins'

import { getActionsConfig } from './actions'
import * as effects from './effects'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Root registry for entity-scoped Organization ECs
 */
@ngCore.Injectable()
export class Feature extends store.Feature<
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  constructor() {
    // NOTE: This has to be injected here so that the functions EC is available
    // in the api service when the feature is initialized, if not the api won't
    // be able to get the attributes
    ngCore.inject(functions.Collection)
    const schemaFeature = ngCore.inject(dms.schema.Feature)

    super({
      actions: getActionsConfig(),
      initialState: State.INITIAL,
      name: 'Space.Organizations.Registry',
      selectors: () => new Selectors(this, schemaFeature),
    })

    const ecDependencies = ngCore.inject(ec.InjectableDependencies)
    const Context$ = ngCore.inject(routeBound.scenario.Context.TOKEN)
    const meFeature = ngCore.inject(me.Feature)
    const surveyAdminsEc = ngCore.inject(surveyAdmins.Collection)

    // Only Platform Admins, Tenant Admins and Survey Admins can edit Joint Use
    const canEditJointUseSg = ngCore.computed(
      (
        canManageUsers = meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
        currentUserId = meFeature.selectors.currentUserId.asSignal(),
        surveyAdminUserIds = new Set(
          surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
            .asSignal()
            .map(({ userId }) => userId),
        ),
      ) =>
        !!currentUserId &&
        (canManageUsers || surveyAdminUserIds.has(currentUserId)),
    )

    this.registerEffects(
      effects.onBootstrap(ecDependencies, Context$, canEditJointUseSg),
      effects.onSchemaChange(schemaFeature),
      effects.onDispose(),
    )
  }

  /** @inheritdoc */
  public override destroy(source: string): void {
    this.actions.dispose.from(source).dispatch({ all: true })
    super.destroy(source)
  }
}
